import * as ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { AlertService } from '@app/@core/services/alert.service';
import { AuthService } from '@app/@core/services/auth.service';
import { HttpService } from '@app/@core/services/http.service';
import { LoaderService } from '@app/@core/services/loader.service';
import { LocalService } from '@app/@core/services/local.service';
import { API_ENDPOINTS } from '@app/shared/constants/apiconstants';
import { DataGridComponent } from '@app/shared/custom-component/data-grid/data-grid.component';
import { AccordionGridSection, AccordionGridSectionManager, ColumnConfig } from '@app/shared/models/accordion-grid-section.model';
import { CommonService } from '@app/shared/services/common.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs';
import { GridActionsComponent } from '@app/shared/custom-component/grid-actions/grid-actions.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AuditTrailsModalComponent } from './modal/audit-trails-modal.component';

@Component({
  selector: 'app-audit-trails',
  standalone: true,
  imports: [DataGridComponent,
    GridActionsComponent,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    MatTooltipModule,
  MatDialogModule],
  templateUrl: './audit-trails.component.html',
  styleUrls: ['./audit-trails.component.scss']
})
export class AuditTrailsComponent implements OnInit {


  auditTrailsHeaders: any[] = [];
  templateOptions: string[] = [];
  private destroy$ = new Subject<void>();
  isLoading: boolean = false;
  isViewOpen: boolean = false;


  // Section manager for accordion grids
  sectionManager = new AccordionGridSectionManager();
  auditTrailsSection: AccordionGridSection;
  auditTrailModalSection: AccordionGridSection;

  // ViewChild references to access data-grid components
  @ViewChild('auditTrailsGrid') auditTrailsGrid: DataGridComponent;

  constructor(
    private commonService: CommonService,
    private readonly alertService: AlertService,
    private modalService: NgbModal,
    private authServices: AuthService,
    private cdr: ChangeDetectorRef,
    private sessionStorage: LocalService,
    private httpService: HttpService,
    private loaderService: LoaderService,
    private dialog: MatDialog
  ) { 
    this.initializeSections()
  }


  ngOnInit() {
    this.loadauditTrails();
  }



  /**
* Utility method to retrieve user ID from various sources
*/
  getUserId(): string | null {
    let userObj = this.commonService.getCurrentUserData();

    if (!userObj) {
      const userFromSession = this.sessionStorage.getItem('user');
      if (userFromSession) {
        userObj = JSON.parse(userFromSession);
      }
    }

    if (!userObj) {
      return null;
    }

    return userObj?.id || userObj?.user_id || userObj?.userId || null;
  }


       private initializeSections(): void {
    // Load Data Template section
    this.loadSectionHeaders(
      'auditTrailsHeaders',
      'Audit Trails',
      '../assets/metadata/audit-trails-header.json' );
  }


  loadauditTrails() {

    this.httpService.get(API_ENDPOINTS.SYSTEM_CONFIG_AUDIT_TRAILS)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          let gridData = []
          if (response?.status === 'success' && response?.data?.data?.p_audit_trails) {
            gridData = response.data.data.p_audit_trails;
            this.sectionManager.updateSectionData('auditTrailsHeaders', gridData);
          } else {
            this.alertService.error('Failed to load audit trails data.');
          }
        },
        error: (error) => {
          this.alertService.error('Failed to load audit trails data. Please try again.');
          this.isLoading = false;
          this.loaderService.hide();
        },
        complete: () => {
          this.isLoading = false;
          this.loaderService.hide();
        }
      })
  }


  /**
        * Load section headers from JSON and create section
        */
  private loadSectionHeaders(
    sectionKey: string,
    sectionTitle: string,
    jsonPath: string,
    showExport: boolean = true,
    showColumnChooser: boolean = true,
    customButtons: any[] = []
  ): void {
    this.commonService
      .getHeaderData(sectionKey, jsonPath)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (headers: any) => {
          if (Array.isArray(headers)) {
            const columns: ColumnConfig[] = headers.map((h: any) => ({
              key: h.dataField,
              label: h.caption || h.dataField,
              disabled: h.dataField === 'application_id'
            }));

            const section = this.sectionManager.createSection(
              sectionKey,
              sectionTitle,
              columns,
              [],
              '',
              showExport,
              showColumnChooser,
              customButtons
            );
            console.log('SECTION', section); // Debug log to check section initialization

            // Assign to the appropriate section property
            if (sectionKey === 'auditTrailsHeaders') this.auditTrailsSection = section;
          }
        }
      });
  }

  // Grid Actions handlers for Data Template
  onAuditTrailsExport(): void {
    this.downloadExcelForSection('auditTrailsHeaders');
  }

  downloadExcelForSection(sectionId: string): void {
    const section = this.sectionManager.getSection(sectionId);
    if (!section) return;

    // Get the appropriate data-grid component based on section ID
    let dataGridComponent: DataGridComponent | null = null;
    if (sectionId === 'auditTrailsHeaders') {
      dataGridComponent = this.auditTrailsGrid;
    }

    // Get filtered data from the data-grid component if available, otherwise use section data
    let rows: any[] = [];
    if (dataGridComponent) {
      rows = dataGridComponent.getFilteredDataFromGrid();
    } else {
      rows = Array.isArray(section.data) ? section.data : [];
    }

    // Filter columns based on visibility
    const columns = section.allColumns.filter(col => section.visibleColumns[col.key]);

    if (columns.length === 0) {
      return;
    }

    if (rows.length === 0) {
      return;
    }

    const header = columns.map(col => col.label);
    const data = rows.map(row => {
      return columns.map(col => {
        const v = row[col.key] ?? row[col.key.toLowerCase()] ?? '';
        return typeof v === 'object' ? JSON.stringify(v) : String(v);
      });
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    const headerRow = worksheet.addRow(header);
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

    data.forEach(row => {
      worksheet.addRow(row);
    });

    worksheet.eachRow(row => {
      row.eachCell(cell => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });
    });

    worksheet.columns.forEach(col => {
      col.width = 30;
    });

    workbook.xlsx.writeBuffer().then(buffer => {
      const fileName = `application-${section.title.replace(/\s+/g, '_')}.xlsx`;
      FileSaver.saveAs(
        new Blob([buffer]),
        fileName
      );
    });
  }

  onAuditTrailsColumnChooserToggle(): void {
    this.sectionManager.toggleColumnChooser('auditTrailsHeaders');
  }

  onAuditTrailsColumnToggle(event: { key: string; col: ColumnConfig }): void {
    this.sectionManager.toggleColumnVisibility('auditTrailsHeaders', event.key);
  }

  onAuditTrailsColumnChooserClose(): void {
    this.sectionManager.closeColumnChooser('auditTrailsHeaders');
  }


  viewTemplate(data: any): void {
    this.dialog.open(AuditTrailsModalComponent, {
      width: '600px',
      data: {
        rowData: data
      }
    });
  }
}
